import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { socket } from '../lib/socket';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface AuthUser {
  id: string;
  name: string;
  activeTitle: string | null;
  activeTheme: string | null;
  activeSoundpack: string | null;
  activeAvatar: string | null;
  coins: number;
  customReactions: { id: string; icon: string; label: string }[] | null;
  reactionUsage: Record<string, number> | null;
  favoriteSoundbites: string[] | null;
  achievements: { achievementKey: string; tier: string }[];
  inventory: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  isLoading: boolean;
  register: (name: string, passphrase: string) => Promise<void>;
  login: (name: string, passphrase: string) => Promise<void>;
  signOut: () => void;
  refreshUser: () => Promise<void>;
  updateName: (name: string) => Promise<void>;
  updateTitle: (title: string | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ACCESS_TOKEN_KEY = 'flip7_access_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(
    () => localStorage.getItem(ACCESS_TOKEN_KEY),
  );
  const [isLoading, setIsLoading] = useState(!!localStorage.getItem(ACCESS_TOKEN_KEY));
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setTokenAndPersist = useCallback((token: string | null) => {
    setAccessToken(token);
    if (token) {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
    socket.auth = token ? { token } : {};
  }, []);

  const signOut = useCallback(() => {
    setUser(null);
    setTokenAndPersist(null);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
  }, [setTokenAndPersist]);

  const refresh = useCallback(async (): Promise<string | null> => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json();
      setUser(data.user);
      setTokenAndPersist(data.accessToken);
      return data.accessToken;
    } catch {
      return null;
    }
  }, [setTokenAndPersist]);

  // Schedule token refresh 13min into 15min token lifetime
  useEffect(() => {
    if (!accessToken) return;
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => { refresh(); }, 13 * 60 * 1000);
    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, [accessToken, refresh]);

  // Refresh token when tab/PWA becomes visible again (timer may have been suspended)
  useEffect(() => {
    if (!accessToken) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [accessToken, refresh]);

  // On mount: try to restore session from existing token or refresh cookie
  useEffect(() => {
    if (!accessToken) {
      // Try refresh cookie
      refresh().then(token => {
        if (!token) setIsLoading(false);
      });
      return;
    }

    fetch(`${API_BASE}/api/users/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => {
        if (r.ok) return r.json();
        return refresh().then(newToken => {
          if (!newToken) return null;
          return fetch(`${API_BASE}/api/users/me`, {
            headers: { Authorization: `Bearer ${newToken}` },
          }).then(r2 => r2.ok ? r2.json() : null);
        });
      })
      .then(data => {
        if (data) setUser(data);
        else setTokenAndPersist(null);
        setIsLoading(false);
      })
      .catch(() => {
        setTokenAndPersist(null);
        setIsLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep socket auth in sync
  useEffect(() => {
    socket.auth = accessToken ? { token: accessToken } : {};
  }, [accessToken]);

  const register = useCallback(async (name: string, passphrase: string) => {
    const res = await fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, passphrase }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Registration failed' }));
      throw new Error(err.message || 'Registration failed');
    }
    const data = await res.json();
    setUser(data.user);
    setTokenAndPersist(data.accessToken);
  }, [setTokenAndPersist]);

  const login = useCallback(async (name: string, passphrase: string) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, passphrase }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Login failed' }));
      throw new Error(err.message || 'Login failed');
    }
    const data = await res.json();
    setUser(data.user);
    setTokenAndPersist(data.accessToken);
  }, [setTokenAndPersist]);

  const refreshUser = useCallback(async () => {
    if (!accessToken) return;
    const res = await fetch(`${API_BASE}/api/users/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.ok) setUser(await res.json());
  }, [accessToken]);

  const updateName = useCallback(async (name: string) => {
    if (!accessToken) return;
    const res = await fetch(`${API_BASE}/api/users/me/name`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ name }),
    });
    if (res.ok) {
      const data = await res.json();
      setUser(prev => prev ? { ...prev, name: data.name } : null);
    }
  }, [accessToken]);

  const updateTitle = useCallback(async (title: string | null) => {
    if (!accessToken) return;
    const res = await fetch(`${API_BASE}/api/users/me/title`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ title }),
    });
    if (res.ok) {
      const data = await res.json();
      setUser(prev => prev ? { ...prev, activeTitle: data.activeTitle } : null);
    }
  }, [accessToken]);

  return (
    <AuthContext.Provider value={{ user, accessToken, isLoading, register, login, signOut, refreshUser, updateName, updateTitle }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
